import { useState, FormEvent } from 'react';
import { api } from '../lib/api';
import { useOnboarding } from '../hooks/useSecurity';

interface Platform {
  name: string;
  github_repo: string;
  criticality: 'critical' | 'high' | 'medium' | 'low';
  target_url: string;
  domains: string;
}

const STEPS = ['Connect GitHub', 'Add Platforms', 'Run First Scan'];

const CRITICALITY_OPTIONS: Platform['criticality'][] = ['critical', 'high', 'medium', 'low'];

const criticalityColor = (c: string) => {
  switch (c) {
    case 'critical': return 'bg-red-100 text-red-700';
    case 'high': return 'bg-orange-100 text-orange-700';
    case 'medium': return 'bg-yellow-100 text-yellow-700';
    default: return 'bg-blue-100 text-blue-700';
  }
};

export default function OnboardingPage() {
  const { data: onboardingData } = useOnboarding();
  const [step, setStep] = useState(0);
  const [platforms, setPlatforms] = useState<Platform[]>([]);
  const [form, setForm] = useState<Platform>({
    name: '', github_repo: '', criticality: 'medium', target_url: '', domains: '',
  });
  const [formError, setFormError] = useState<string | null>(null);
  const [addingPlatform, setAddingPlatform] = useState(false);
  const [scanTriggered, setScanTriggered] = useState(false);
  const [scanLoading, setScanLoading] = useState(false);

  const githubInstalled = (onboardingData as Record<string, unknown>)?.github_connected === true;

  async function saveProgress(completedStep: number, extra?: Record<string, unknown>) {
    await api.put('/onboarding', {
      step: completedStep,
      ...extra,
    });
  }

  function handleGithubConnect() {
    window.open(
      `https://github.com/apps/the-one-security/installations/new`,
      '_blank',
      'width=1024,height=768',
    );
  }

  async function handleGithubDone() {
    try {
      await saveProgress(1, { github_connected: true });
      setStep(1);
    } catch {
      setStep(1);
    }
  }

  async function handleAddPlatform(e: FormEvent) {
    e.preventDefault();
    setFormError(null);

    if (!form.name.trim() || !form.github_repo.trim()) {
      setFormError('Name and GitHub repository are required.');
      return;
    }
    if (!/^[\w-]+\/[\w.-]+$/.test(form.github_repo.trim())) {
      setFormError('GitHub repo must be in the format "owner/repo".');
      return;
    }

    setAddingPlatform(true);
    try {
      const domainsArr = form.domains
        .split(',')
        .map(d => d.trim())
        .filter(Boolean);

      await api.post('/platforms', {
        name: form.name,
        github_repo: form.github_repo,
        criticality: form.criticality,
        target_url: form.target_url || undefined,
        domains: domainsArr,
      });

      setPlatforms(prev => [...prev, form]);
      setForm({ name: '', github_repo: '', criticality: 'medium', target_url: '', domains: '' });
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to add platform');
    } finally {
      setAddingPlatform(false);
    }
  }

  async function handleProceedToScan() {
    await saveProgress(2, { platforms_added: platforms.length });
    setStep(2);
  }

  async function handleTriggerScan() {
    setScanLoading(true);
    try {
      await api.post('/security/scans/trigger', { scan_all: true });
      await saveProgress(3, { first_scan_triggered: true });
      setScanTriggered(true);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to trigger scan');
    } finally {
      setScanLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="flex justify-center mb-4">
            <div className="w-12 h-12 bg-indigo-600 rounded-xl flex items-center justify-center">
              <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
          </div>
          <h1 className="text-3xl font-extrabold text-gray-900">Welcome to The One Security</h1>
          <p className="mt-2 text-gray-500">Let's get your security monitoring set up in 3 steps.</p>
        </div>

        {/* Step Indicator */}
        <div className="flex items-center justify-center mb-10">
          {STEPS.map((label, i) => (
            <div key={i} className="flex items-center">
              <div className={`flex flex-col items-center ${i < STEPS.length - 1 ? 'w-32 sm:w-48' : ''}`}>
                <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold border-2 transition-colors ${
                  i < step ? 'bg-indigo-600 border-indigo-600 text-white'
                  : i === step ? 'border-indigo-600 text-indigo-600 bg-white'
                  : 'border-gray-300 text-gray-400 bg-white'
                }`}>
                  {i < step ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : i + 1}
                </div>
                <span className={`mt-1 text-xs font-medium ${i === step ? 'text-indigo-600' : 'text-gray-400'}`}>
                  {label}
                </span>
              </div>
              {i < STEPS.length - 1 && (
                <div className={`h-0.5 flex-1 mx-2 mb-5 ${i < step ? 'bg-indigo-600' : 'bg-gray-200'}`} />
              )}
            </div>
          ))}
        </div>

        {/* Step 0: Connect GitHub */}
        {step === 0 && (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <div className="w-16 h-16 bg-gray-900 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">Connect Your GitHub Account</h2>
            <p className="text-gray-500 mb-2 max-w-md mx-auto">
              Install the The One Security GitHub App to enable automated vulnerability scanning, SAST analysis, and dependency tracking across your repositories.
            </p>
            <ul className="text-sm text-gray-500 mb-8 space-y-1">
              <li>• Read access to code and pull requests</li>
              <li>• Receive webhook notifications on pushes</li>
              <li>• No write access required</li>
            </ul>
            <button
              onClick={handleGithubConnect}
              className="bg-gray-900 text-white px-8 py-3 rounded-md font-medium hover:bg-gray-800 inline-flex items-center gap-2 mb-4"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" />
              </svg>
              Install GitHub App
            </button>
            <br />
            <button
              onClick={handleGithubDone}
              className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
            >
              {githubInstalled ? 'GitHub connected — Continue' : "I've installed the app, continue"}
            </button>
          </div>
        )}

        {/* Step 1: Add Platforms */}
        {step === 1 && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-1">Add Your Platforms</h2>
              <p className="text-sm text-gray-500 mb-6">
                Add the applications and services you want to monitor. You can add more from Settings later.
              </p>

              {formError && (
                <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-3">
                  <p className="text-sm text-red-700">{formError}</p>
                </div>
              )}

              <form onSubmit={handleAddPlatform} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Platform Name *</label>
                    <input
                      type="text"
                      required
                      value={form.name}
                      onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="My Web App"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">GitHub Repository *</label>
                    <input
                      type="text"
                      required
                      value={form.github_repo}
                      onChange={e => setForm(f => ({ ...f, github_repo: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="owner/repo"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Criticality</label>
                    <select
                      value={form.criticality}
                      onChange={e => setForm(f => ({ ...f, criticality: e.target.value as Platform['criticality'] }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      {CRITICALITY_OPTIONS.map(c => (
                        <option key={c} value={c} className="capitalize">{c.charAt(0).toUpperCase() + c.slice(1)}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Target URL (for DAST)</label>
                    <input
                      type="url"
                      value={form.target_url}
                      onChange={e => setForm(f => ({ ...f, target_url: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="https://app.example.com"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Domains (comma-separated)</label>
                  <input
                    type="text"
                    value={form.domains}
                    onChange={e => setForm(f => ({ ...f, domains: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="example.com, api.example.com"
                  />
                </div>
                <button
                  type="submit"
                  disabled={addingPlatform}
                  className="bg-indigo-600 text-white px-5 py-2 rounded-md text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 inline-flex items-center gap-2"
                >
                  {addingPlatform ? (
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                  )}
                  Add Platform
                </button>
              </form>
            </div>

            {/* Added platforms list */}
            {platforms.length > 0 && (
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="font-semibold text-gray-900">Added Platforms ({platforms.length})</h3>
                </div>
                <ul className="divide-y divide-gray-100">
                  {platforms.map((p, i) => (
                    <li key={i} className="px-6 py-4 flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900 text-sm">{p.name}</p>
                        <p className="text-xs text-gray-500">{p.github_repo}</p>
                      </div>
                      <span className={`rounded-full px-2 py-1 text-xs font-medium capitalize ${criticalityColor(p.criticality)}`}>
                        {p.criticality}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex justify-between">
              <button onClick={() => setStep(0)} className="text-sm text-gray-500 hover:text-gray-700">
                Back
              </button>
              <button
                onClick={handleProceedToScan}
                disabled={platforms.length === 0}
                className="bg-indigo-600 text-white px-6 py-2 rounded-md text-sm font-medium hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Continue to Scan Setup
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Trigger First Scan */}
        {step === 2 && (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            {!scanTriggered ? (
              <>
                <div className="w-16 h-16 bg-indigo-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <svg className="w-9 h-9 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">Trigger Your First Scan</h2>
                <p className="text-gray-500 mb-2 max-w-md mx-auto">
                  We'll kick off vulnerability scans, SAST analysis, and dependency checks across all{' '}
                  <strong>{platforms.length}</strong> platform{platforms.length !== 1 ? 's' : ''} you added.
                </p>
                <p className="text-sm text-gray-400 mb-8">
                  Initial scans typically take 5–15 minutes. You'll see results in your dashboard shortly after.
                </p>
                {formError && (
                  <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-3 text-left max-w-md mx-auto">
                    <p className="text-sm text-red-700">{formError}</p>
                  </div>
                )}
                <button
                  onClick={handleTriggerScan}
                  disabled={scanLoading}
                  className="bg-indigo-600 text-white px-8 py-3 rounded-md font-medium hover:bg-indigo-700 disabled:opacity-50 inline-flex items-center gap-2"
                >
                  {scanLoading ? (
                    <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                  ) : null}
                  {scanLoading ? 'Starting scans...' : 'Start First Scan'}
                </button>
                <div className="mt-4">
                  <button onClick={() => setStep(1)} className="text-sm text-gray-400 hover:text-gray-600">
                    Back
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <svg className="w-9 h-9 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">You're all set!</h2>
                <p className="text-gray-500 mb-8 max-w-md mx-auto">
                  Scans are running in the background. Your dashboard will populate with results within a few minutes.
                </p>
                <a
                  href="/"
                  className="bg-indigo-600 text-white px-8 py-3 rounded-md font-medium hover:bg-indigo-700 inline-block"
                >
                  Go to Dashboard
                </a>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
